import { __decorate } from "tslib";
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import HelixElement from '../../shared/types/helix-element.js';
import { localized } from '@lit/localize';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { menuStyles } from './menu.styles.js';
import { createRef, ref } from 'lit-html/directives/ref.js';
import { autoUpdate, computePosition, flip, offset, size } from '@floating-ui/dom';
import { HlxMenuItem } from './menu-item.js';
import { HlxMenuGroup } from './menu-group.js';
import { provide } from '@lit/context';
import { dividersContext, groupLabelsContext, menuSizeContext } from './menu-context.js';
import { watch } from '../../shared/directives/watch.decorator.js';
import { PopupSearchStringManager } from '../../shared/popup-search-string-manager.js';
let HlxMenu = class HlxMenu extends HelixElement {
    constructor() {
        super(...arguments);
        this.minWidth = 0;
        this.dividers = false;
        this.size = 'medium';
        this.placement = 'bottom-start';
        this.keepOpenOnItemClick = false;
        this.open = false;
        this.groupLabelsPresent = false;
        this.dialogRef = createRef();
        this.controlRef = createRef();
        this.itemContainerRef = createRef();
        this.searchStringManager = new PopupSearchStringManager();
    }
    render() {
        return html `
            <slot name="control" @click=${this.toggleMenu} ${ref(this.controlRef)}></slot>
            <dialog
                ${ref(this.dialogRef)}
                class="hlx-menu-dialog hlx-animation-with-prefers-reduced-motion"
                @focusout=${this.handleFocusOut}>
                <div
                    role="menu"
                    class=${classMap({
            'hlx-menu-popup': true,
            'hlx-menu-with-dividers': this.dividers,
        })}
                    style="min-width: ${this.minWidth}px"
                    ${ref(this.itemContainerRef)}>
                    <slot
                        @slotchange=${this.updateMenuItems}
                        @hlx-menu-item-focus=${this.handleItemFocus}
                        @hlx-menu-close=${this.handleClose}
                        @hlx-click=${this.handleItemClick}></slot>
                </div>
            </dialog>
        `;
    }
    show() {
        this.open = true;
        this.emit('hlx-show');
    }
    close() {
        this.open = false;
        this.emit('hlx-hide');
    }
    focusFirstItem() {
        if (this.open === this.dialogRef.value?.open || !this.dialogRef.value) {
            return;
        }
        if (this.open) {
            this.dialogRef.value.show();
            this.menuItems[0]?.focus();
        }
        else {
            this.dialogRef.value.close();
        }
    }
    async handlePlacementChange() {
        await this.initPopup();
    }
    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        this.cleanupPopup = autoUpdate(this.controlRef.value, this.dialogRef.value, () => this.initPopup());
    }
    connectedCallback() {
        super.connectedCallback();
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.addEventListener('keydown', this.handleKeyDown);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanupPopup();
        this.removeEventListener('keydown', this.handleKeyDown);
    }
    updateMenuItems(slotChange) {
        if (!(slotChange.target instanceof HTMLSlotElement)) {
            return;
        }
        const slot = slotChange.target;
        this.menuItems = slot
            .assignedElements()
            .map((element) => {
            if (element instanceof HlxMenuGroup) {
                return element.getAssignedItems();
            }
            return element;
        })
            .flat()
            .filter((element) => element instanceof HlxMenuItem);
        this.searchStringManager.options = this.menuItems.filter((item) => !item.disabled);
        this.groupLabelsPresent = slot
            .assignedElements()
            .some((element) => element instanceof HlxMenuGroup && element.hasLabel());
    }
    handleItemFocus(event) {
        const focusableItems = this.menuItems.filter((item) => !item.disabled);
        if (focusableItems.length === 0 || !(event.target instanceof HlxMenuItem)) {
            return;
        }
        const directionToDelta = {
            prev: -1,
            next: 1,
            none: 0,
        };
        const nextIndex = focusableItems.indexOf(event.target) + (directionToDelta[event.detail.direction] ?? 0);
        if (nextIndex < 0 || nextIndex >= focusableItems.length) {
            return;
        }
        else {
            focusableItems[nextIndex].focus();
        }
    }
    handleClose(event) {
        event.stopPropagation();
        this.close();
    }
    handleFocusOut(event) {
        const isFocusingEnabledOption = event.relatedTarget instanceof HlxMenuItem &&
            !event.relatedTarget.disabled &&
            this.menuItems.includes(event.relatedTarget);
        const isFocusingControl = this.controlRef.value
            .assignedElements()
            .find((element) => element === event.relatedTarget ||
            (event.relatedTarget instanceof Element &&
                element.contains(event.relatedTarget)));
        if (isFocusingEnabledOption || isFocusingControl) {
            return;
        }
        this.close();
    }
    handleItemClick() {
        if (this.keepOpenOnItemClick) {
            return;
        }
        this.close();
    }
    handleKeyDown(event) {
        if (event.code === 'Escape' && this.open) {
            event.preventDefault();
            this.close();
            return;
        }
        if (this.searchStringManager.isPrintableChar(event) &&
            !this.open &&
            // Space will be handled as click, no need to open the menu here:
            event.code !== 'Space') {
            this.show();
            // the menu items are not focusable right away when the dialog is open,
            // so we just open it and not continue the search until next keyboard event
            return;
        }
        const focusedItem = this.menuItems.find((item) => item.matches(':focus'));
        const optionToFocusFromSearchString = this.searchStringManager.getOptionToFocus(event, focusedItem);
        optionToFocusFromSearchString?.focus();
    }
    toggleMenu() {
        if (this.open) {
            this.close();
        }
        else {
            this.show();
        }
    }
    async initPopup() {
        const itemContainer = this.itemContainerRef.value;
        const control = this.controlRef.value.assignedElements()[0];
        if (!control) {
            return;
        }
        const { x, y } = await computePosition(control, this.dialogRef.value, {
            placement: this.placement,
            middleware: [
                offset(8),
                flip(),
                size({
                    padding: 8,
                    apply({ availableWidth, availableHeight, elements }) {
                        if (!itemContainer) {
                            return;
                        }
                        Object.assign(itemContainer.style, {
                            maxWidth: `${availableWidth}px`,
                            maxHeight: `${availableHeight}px`,
                        });
                    },
                }),
            ],
        });
        Object.assign(this.dialogRef.value.style, {
            left: `${x}px`,
            top: `${y}px`,
        });
    }
};
HlxMenu.styles = [componentStyles, menuStyles];
__decorate([
    property({ type: Number, attribute: 'min-width' })
], HlxMenu.prototype, "minWidth", void 0);
__decorate([
    provide({ context: dividersContext }),
    property({ type: Boolean })
], HlxMenu.prototype, "dividers", void 0);
__decorate([
    provide({ context: menuSizeContext }),
    property({ type: String })
], HlxMenu.prototype, "size", void 0);
__decorate([
    property({ type: String })
], HlxMenu.prototype, "placement", void 0);
__decorate([
    property({ type: Boolean, attribute: 'keep-open-on-item-click' })
], HlxMenu.prototype, "keepOpenOnItemClick", void 0);
__decorate([
    property({ type: Boolean })
], HlxMenu.prototype, "open", void 0);
__decorate([
    provide({ context: groupLabelsContext })
], HlxMenu.prototype, "groupLabelsPresent", void 0);
__decorate([
    watch('open')
], HlxMenu.prototype, "focusFirstItem", null);
__decorate([
    watch('placement', { waitUntilFirstUpdate: true })
], HlxMenu.prototype, "handlePlacementChange", null);
HlxMenu = __decorate([
    customElement('hlx-menu'),
    localized()
], HlxMenu);
export { HlxMenu };
//# sourceMappingURL=menu.js.map